import { i18n } from '@lingui/core';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { sendIpc } from 'pages/vo/vo-react/ipc/send-ipc';
import { isDesktopApp, isLocalhost } from 'utils/client-utils';
import { initCrashReporter } from 'utils/crash-reporter';
import { initializeFirebase } from 'utils/firebase-app';
import { initializePopDevTools } from 'utils/pop-devtools';
import { installConsoleTap } from 'utils/pop-devtools/taps/console-tap';
import { initializePopDevToolsTaps } from 'utils/pop-devtools/taps/init-taps';
import { previewPr } from 'utils/preview-origin';
import { registerServiceWorker, setupVitePreloadErrorHandler } from 'utils/service-worker';

import { router } from './pages/vo/vo-react/router';

if (__POP_DEVTOOLS__) {
  initializePopDevTools();
  installConsoleTap();
  initializePopDevToolsTaps();
}

// PR previews (docs/PR_PREVIEW_PLAN_V4.md): make it obvious which build a reviewer is looking at.
if (previewPr) document.title = `PR ${previewPr} · ${document.title}`;

void initCrashReporter();
registerServiceWorker();
setupVitePreloadErrorHandler();
initializeFirebase({ shouldUseLocalhostAuth: __USE_LOCAL_AUTH__ });

async function getLocaleFile() {
  const detectedLocale = navigator.language.split('-')[0];

  if (detectedLocale === 'fr') {
    return { locale: 'fr', file: await import(`assets/locales/fr.po`) };
  }
  return { locale: 'en', file: await import(`assets/locales/en.po`) };
}

async function boot() {
  if (__USE_LOCAL_AUTH__) {
    const { initializeDevAutoLogin } = await import('utils/dev-auto-login');
    // The router is constructed at module load but does not render until after this awaits. Clearing
    // persisted auth here therefore guarantees the native gate starts signed out when onboarding is
    // intentionally enabled.
    const launchInfo = isDesktopApp ? await sendIpc('getAppLaunchInfo') : undefined;
    await initializeDevAutoLogin({
      shouldUseLocalAuth: true,
      isDesktopApp,
      isLocalhost,
      skipOnboarding: !!launchInfo?.automation.skipOnboarding,
    });
  }

  const {
    locale,
    file: { messages },
  } = await getLocaleFile();
  i18n.load(locale, messages);
  i18n.activate(locale);

  createRoot(document.getElementById('root') as HTMLElement).render(<RouterProvider router={router} />);
}

void boot();
